import React from 'react';
import ErrorWrapper from '@/app/ErrorWrapper';
import { notification, Table } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { studentsApi } from '../../../api/students/studentsApi';
import { dateStringToYYYYMMDD } from '../../../utils/functions';
import { authApi, documentsApi, filesApi } from '@/api';
import { tokenTypesApi } from '@/api/tokens/tokenTypesApi';
import { tokensApi } from '@/api/tokens/tokensApi';
import { Avatar } from '../../../components/Avatar/Avatar';
import axios from 'axios';
import { DOCUMENT_TYPE, STUDENT_STATUS, STUDENT_TEST_TYPE } from '@/utils/constants';
import { useTranslation } from 'react-i18next'; // Added import

const EditComponent = ({ ...props }) => {
    const { t } = useTranslation(); // Added hook
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        handleSubmit,
        control,
        reset,
        watch,
        setValue,
        unregister,
        formState: { errors }
    } = useForm({
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            dateOfBirth: null,
            gender: 'Male',
            phone: '',
            status: 'NOT_TESTED',
            avatar: null,
            password: ''
        }
    });
    const statusValue = watch('status');
    const recommendationValue = watch('courseRecommendation');
    const feedbackValue = watch('feedback');
    const id = searchParams.get('id');
    const [isLoadingStudentDetail, setIsLoadingStudentDetail] = useState(false);
    const [tokens, setTokens] = useState([]);
    const [tokensUsed, setTokensUsed] = useState([]);
    const [tokenTopupData, setTokenTopupData] = useState({
        studentId: id,
        tokenTypeId: '',
        amount: 0,
        description: 'Admin top-up'
    });

    const [tokenTypeData, setTokenTypeData] = useState([]);
    const [isLoadingTokenTypeData, setIsLoadingTokenTypeData] = useState(false);
    const [paginationTokenTypeData, setPaginationTokenTypeData] = useState({
        page: 1,
        pageSize: 10,
        total: 0
    });
    const [avatarUrl, setAvatarUrl] = useState('');
    const [uploadedFile, setUploadedFile] = useState();

    const getListTokenTypes = () => {
        setIsLoadingTokenTypeData(true);
        tokenTypesApi
            .getList({
                page: paginationTokenTypeData.page,
                pageSize: paginationTokenTypeData.pageSize
            })
            .then((res) => {
                const tokenTypes = res.data.data.rows;
                setIsLoadingTokenTypeData(false);
                setTokenTypeData(tokenTypes);
                setPaginationTokenTypeData({
                    ...paginationTokenTypeData,
                    total: res.data.data.total
                });

                if (tokenTypes.length > 0) {
                    setTokenTopupData((prev) => ({
                        ...prev,
                        tokenTypeId: tokenTypes[0].id
                    }));
                }
            })
            .catch((err) => console.error(err));
    };

    const fetchStudentDetail = () => {
        if (id) {
            setIsLoadingStudentDetail(true);
            studentsApi
                .getDetail(id)
                .then((res) => {
                    const data = res.data.data;
                    setTokens(data?.tokens || []);
                    setTokensUsed(data?.tokensUsed || []);
                    setAvatarUrl(data?.avatar);
                    reset({
                        firstName: data?.firstName,
                        lastName: data?.lastName,
                        email: data?.email,
                        dateOfBirth: data?.dateOfBirth ? moment(data?.dateOfBirth).toDate() : null,
                        gender: data?.gender,
                        phone: data?.phone,
                        status: data?.status,
                        courseRecommendation: data?.courseRecommendation,
                        feedback: data?.feedback
                    });
                })
                .catch((err) => console.error(err))
                .finally(() => setIsLoadingStudentDetail(false));
        }
    };

    useEffect(() => {
        fetchStudentDetail();
    }, [id]);

    useEffect(() => {
        if (!recommendationValue && statusValue == STUDENT_STATUS.TESTED) {
            setValue('courseRecommendation', STUDENT_TEST_TYPE[0].key);
        } else {
            unregister('courseRecommendation');
        }

        if (!feedbackValue && statusValue == STUDENT_STATUS.TESTED) {
            setValue('feedback', '');
        } else {
            unregister('feedback');
        }
    }, [statusValue]);

    useEffect(() => {
        getListTokenTypes();
    }, []);

    const submitForm = (formPayload) => {
        setIsLoadingStudentDetail(true);
        const apiCall = id
            ? studentsApi.update({
                  ...formPayload,
                  id: id,
                  dateOfBirth: dateStringToYYYYMMDD(formPayload.dateOfBirth)
              })
            : authApi.register({
                  ...formPayload,
                  dateOfBirth: dateStringToYYYYMMDD(formPayload.dateOfBirth)
              });

        apiCall
            .then((res) => {
                notification.success({
                    message: id
                        ? t('Student_Admin_Edit_Submit_UpdateSuccess')
                        : t('Student_Admin_Edit_Submit_CreateSuccess')
                });
                if (uploadedFile) {
                    documentsApi.create({
                        name: 'Student Avatar',
                        fileType: uploadedFile.type,
                        documentType: DOCUMENT_TYPE.AVATAR,
                        fileName: uploadedFile.name,
                        entityName: 'students',
                        entityId: id ? id : res?.data?.data?.id
                    });
                }
                if (id) {
                    fetchStudentDetail();
                } else {
                    reset();
                }
            })
            .catch((err) => notification.error({ message: err.response.data.message }))
            .finally(() => {
                setIsLoadingStudentDetail(false);
            });
    };

    const onFormError = (errors) => {
        console.error('Form validation errors:', errors);
        let errorMsg = t('Student_Admin_Edit_Validation_GeneralError');
        if (errors.avatar) {
            errorMsg = t('Student_Admin_Edit_Validation_AvatarError');
        }

        notification.warning({
            message: t('Student_Admin_Edit_Validation_MissingFieldsTitle'),
            description: errorMsg
        });
    };

    const handleAvatarChange = (file) => {
        if (file instanceof File) {
            filesApi
                .upload({
                    fileName: file.name,
                    fileType: file.type,
                    entityName: 'students'
                })
                .then((res) => {
                    notification.info({ message: t('Student_Admin_Edit_Avatar_Uploading') });
                    return axios.put(res?.data?.data?.url, file, {
                        headers: {
                            'Content-Type': file.type
                        }
                    });
                })
                .then(() => {
                    filesApi
                        .view({
                            fileName: file.name,
                            entityName: 'students'
                        })
                        .then((res) => {
                            setAvatarUrl(res?.data?.data?.url);
                            notification.success({
                                message: t('Student_Admin_Edit_Avatar_Success')
                            });
                        });

                    setUploadedFile(file);
                })
                .catch((err) => {
                    console.error('Avatar upload error:', err);
                    notification.error({
                        message: t('Student_Admin_Edit_Avatar_FailedTitle'),
                        description:
                            err.response?.data?.message ||
                            t('Student_Admin_Edit_Avatar_FailedDescription')
                    });
                });
        }
    };

    return (
        <>
            <div className="section-body"></div>
            <div className="section-body mt-4">
                <div className="row clearfix">
                    {isLoadingStudentDetail ? (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                minHeight: '100px'
                            }}>
                            <Spinner
                                size={'sm'}
                                children=""
                            />
                        </div>
                    ) : (
                        <>
                            <div className="col-lg-8 col-md-12 col-sm-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">
                                            {t('Student_Admin_Edit_BasicInfo_Title')}
                                        </h3>
                                    </div>
                                    <form
                                        onSubmit={handleSubmit(submitForm, onFormError)}
                                        className="card-body">
                                        <div className="mx-auto flex flex-col items-center justify-center pt-3 pb-3">
                                            <Avatar
                                                value={avatarUrl}
                                                onChange={(file) => handleAvatarChange(file)}
                                                size={100}
                                            />
                                        </div>
                                        <div className="form-group row">
                                            <label className="col-md-3 col-form-label">
                                                {t('Student_Admin_Edit_Form_FirstName')}{' '}
                                                <span className="text-danger">*</span>
                                            </label>
                                            <div className="col-md-9">
                                                <Controller
                                                    control={control}
                                                    name="firstName"
                                                    rules={{
                                                        required: t(
                                                            'Student_Admin_Edit_Validation_FirstNameRequired'
                                                        )
                                                    }}
                                                    render={({
                                                        field: { onChange, onBlur, value }
                                                    }) => (
                                                        <input
                                                            type="text"
                                                            className={`form-control ${
                                                                errors.firstName ? 'is-invalid' : ''
                                                            }`}
                                                            placeholder={t(
                                                                'Student_Admin_Edit_Form_FirstNamePlaceholder'
                                                            )}
                                                            value={value}
                                                            onChange={onChange}
                                                            onBlur={onBlur}
                                                        />
                                                    )}
                                                />
                                                {errors.firstName && (
                                                    <div className="invalid-feedback">
                                                        {errors.firstName.message}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-group row">
                                            <label className="col-md-3 col-form-label">
                                                {t('Student_Admin_Edit_Form_LastName')}{' '}
                                                <span className="text-danger">*</span>
                                            </label>
                                            <div className="col-md-9">
                                                <Controller
                                                    control={control}
                                                    name="lastName"
                                                    rules={{
                                                        required: t(
                                                            'Student_Admin_Edit_Validation_LastNameRequired'
                                                        )
                                                    }}
                                                    render={({
                                                        field: { onChange, onBlur, value }
                                                    }) => (
                                                        <input
                                                            type="text"
                                                            className={`form-control ${
                                                                errors.lastName ? 'is-invalid' : ''
                                                            }`}
                                                            placeholder={t(
                                                                'Student_Admin_Edit_Form_LastNamePlaceholder'
                                                            )}
                                                            value={value}
                                                            onChange={onChange}
                                                            onBlur={onBlur}
                                                        />
                                                    )}
                                                />
                                                {errors.lastName && (
                                                    <div className="invalid-feedback">
                                                        {errors.lastName.message}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-group row">
                                            <label className="col-md-3 col-form-label">
                                                {t('Student_Admin_Edit_Form_Gender')}{' '}
                                                <span className="text-danger">*</span>
                                            </label>
                                            <div className="col-md-9">
                                                <Controller
                                                    control={control}
                                                    name="gender"
                                                    render={({
                                                        field: { onChange, onBlur, value }
                                                    }) => (
                                                        <select
                                                            className="form-control show-tick"
                                                            value={value}
                                                            onChange={onChange}
                                                            onBlur={onBlur}>
                                                            <option value="Male">
                                                                {t(
                                                                    'Student_Admin_Edit_Form_GenderMale'
                                                                )}
                                                            </option>
                                                            <option value="Female">
                                                                {t(
                                                                    'Student_Admin_Edit_Form_GenderFemale'
                                                                )}
                                                            </option>
                                                        </select>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group row">
                                            <label className="col-md-3 col-form-label">
                                                {t('Student_Admin_Edit_Form_Email')}{' '}
                                                <span className="text-danger">*</span>
                                            </label>
                                            <div className="col-md-9">
                                                <Controller
                                                    control={control}
                                                    name="email"
                                                    rules={{
                                                        required: t(
                                                            'Student_Admin_Edit_Validation_EmailRequired'
                                                        ),
                                                        pattern: {
                                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                            message: t(
                                                                'Student_Admin_Edit_Validation_EmailInvalid'
                                                            )
                                                        }
                                                    }}
                                                    render={({
                                                        field: { onChange, onBlur, value }
                                                    }) => (
                                                        <input
                                                            type="email"
                                                            className={`form-control ${
                                                                errors.email ? 'is-invalid' : ''
                                                            }`}
                                                            value={value}
                                                            onChange={onChange}
                                                            onBlur={onBlur}
                                                            disabled={!!id}
                                                        />
                                                    )}
                                                />
                                                {errors.email && (
                                                    <div className="invalid-feedback">
                                                        {errors.email.message}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-group row">
                                            <label className="col-md-3 col-form-label">
                                                {t('Student_Admin_Edit_Form_DOB')}{' '}
                                                <span className="text-danger">*</span>
                                            </label>
                                            <div className="col-md-9">
                                                <Controller
                                                    control={control}
                                                    name="dateOfBirth"
                                                    rules={{
                                                        required: t(
                                                            'Student_Admin_Edit_Validation_DOBRequired'
                                                        )
                                                    }}
                                                    render={({
                                                        field: { onChange, onBlur, value, ref }
                                                    }) => (
                                                        <>
                                                            <DatePicker
                                                                className={`form-control ${
                                                                    errors.dateOfBirth
                                                                        ? 'is-invalid'
                                                                        : ''
                                                                }`}
                                                                onChange={onChange}
                                                                onBlur={onBlur}
                                                                selected={value}
                                                                dateFormat="MM/dd/yyyy"
                                                                placeholderText={t(
                                                                    'Student_Admin_Edit_Form_DOBPlaceholder'
                                                                )}
                                                            />
                                                            {errors.dateOfBirth && (
                                                                <div
                                                                    className="text-danger"
                                                                    style={{
                                                                        fontSize: '80%',
                                                                        marginTop: '0.25rem'
                                                                    }}>
                                                                    {errors.dateOfBirth.message}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group row">
                                            <label className="col-md-3 col-form-label">
                                                {t('Student_Admin_Edit_Form_Phone')}{' '}
                                                <span className="text-danger">*</span>
                                            </label>
                                            <div className="col-md-9">
                                                <Controller
                                                    control={control}
                                                    name="phone"
                                                    rules={{
                                                        required: t(
                                                            'Student_Admin_Edit_Validation_PhoneRequired'
                                                        )
                                                    }}
                                                    render={({
                                                        field: { onChange, onBlur, value }
                                                    }) => (
                                                        <input
                                                            type="tel"
                                                            className={`form-control ${
                                                                errors.phone ? 'is-invalid' : ''
                                                            }`}
                                                            value={value}
                                                            onChange={onChange}
                                                            onBlur={onBlur}
                                                        />
                                                    )}
                                                />
                                                {errors.phone && (
                                                    <div className="invalid-feedback">
                                                        {errors.phone.message}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-group row">
                                            <label className="col-md-3 col-form-label">
                                                {t('Student_Admin_Edit_Form_Status')}{' '}
                                                <span className="text-danger">*</span>
                                            </label>
                                            <div className="col-md-9">
                                                <Controller
                                                    control={control}
                                                    name="status"
                                                    render={({
                                                        field: { onChange, onBlur, value }
                                                    }) => (
                                                        <select
                                                            className="form-control show-tick"
                                                            value={value}
                                                            onChange={onChange}
                                                            onBlur={onBlur}>
                                                            <option value="NOT_TESTED">
                                                                {t(
                                                                    'Student_Admin_Edit_Status_NotTested'
                                                                )}
                                                            </option>
                                                            <option value="TESTED">
                                                                {t(
                                                                    'Student_Admin_Edit_Status_Tested'
                                                                )}
                                                            </option>
                                                            <option value="STUDYING">
                                                                {t(
                                                                    'Student_Admin_Edit_Status_Studying'
                                                                )}
                                                            </option>
                                                            <option value="COMPLETED">
                                                                {t(
                                                                    'Student_Admin_Edit_Status_Completed'
                                                                )}
                                                            </option>
                                                            <option value="ON_LEAVE">
                                                                {t(
                                                                    'Student_Admin_Edit_Status_Inactive'
                                                                )}
                                                            </option>
                                                        </select>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        {statusValue == STUDENT_STATUS.TESTED && (
                                            <>
                                                <div className="form-group row">
                                                    <label className="col-md-3 col-form-label">
                                                        {t('Student_Admin_Edit_Form_TestType')}
                                                    </label>
                                                    <div className="col-md-9">
                                                        <Controller
                                                            control={control}
                                                            name="courseRecommendation"
                                                            render={({
                                                                field: { onChange, onBlur, value }
                                                            }) => (
                                                                <select
                                                                    className="form-control show-tick"
                                                                    value={value}
                                                                    onChange={onChange}
                                                                    onBlur={onBlur}>
                                                                    {STUDENT_TEST_TYPE.map((e) => {
                                                                        return (
                                                                            <option value={e.key}>
                                                                                {e.label}
                                                                            </option>
                                                                        );
                                                                    })}
                                                                </select>
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-group row">
                                                    <label className="col-md-3 col-form-label">
                                                        {t('Student_Admin_Edit_Form_Feedback')}
                                                    </label>
                                                    <div className="col-md-9">
                                                        <Controller
                                                            control={control}
                                                            name="feedback"
                                                            render={({ field }) => (
                                                                <textarea
                                                                    rows="4"
                                                                    className="form-control no-resize"
                                                                    placeholder={t(
                                                                        'Student_Admin_Edit_Form_FeedbackPlaceholder'
                                                                    )}
                                                                    {...field}></textarea>
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </form>
                                </div>
                            </div>
                            <div className="col-lg-4 col-md-12 col-sm-12">
                                {!id && (
                                    <div className="card">
                                        <div className="card-header">
                                            <h3 className="card-title">
                                                {t('Student_Admin_Edit_AccountInfo_Title')}
                                            </h3>
                                        </div>
                                        <div className="card-body">
                                            <div className="row clearfix">
                                                <div className="col-sm-12">
                                                    <div className="form-group">
                                                        <label>
                                                            {t('Student_Admin_Edit_Form_UserName')}
                                                        </label>
                                                        <Controller
                                                            control={control}
                                                            name="email"
                                                            render={({ field: { value } }) => (
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={value}
                                                                    disabled
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-6 col-sm-12">
                                                    <div className="form-group">
                                                        <label>
                                                            {t('Student_Admin_Edit_Form_Password')}{' '}
                                                            {!id && (
                                                                <span className="text-danger">
                                                                    *
                                                                </span>
                                                            )}
                                                        </label>
                                                        <Controller
                                                            control={control}
                                                            name="password"
                                                            rules={{
                                                                required: !id
                                                                    ? t(
                                                                          'Student_Admin_Edit_Validation_PasswordRequired'
                                                                      )
                                                                    : false
                                                            }}
                                                            render={({
                                                                field: { onChange, onBlur, value }
                                                            }) => (
                                                                <input
                                                                    type="password"
                                                                    className={`form-control ${
                                                                        errors.password
                                                                            ? 'is-invalid'
                                                                            : ''
                                                                    }`}
                                                                    value={value}
                                                                    onChange={onChange}
                                                                    onBlur={onBlur}
                                                                    disabled={!!id}
                                                                />
                                                            )}
                                                        />
                                                        {errors.password && (
                                                            <div className="invalid-feedback">
                                                                {errors.password.message}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6 col-sm-12">
                                                    <div className="form-group">
                                                        <label>
                                                            {t(
                                                                'Student_Admin_Edit_Form_ConfirmPassword'
                                                            )}
                                                        </label>
                                                        <input
                                                            type="password"
                                                            className="form-control"
                                                            disabled={!!id}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {id && (
                                    <div className="card">
                                        <h3 className="card-title pl-[20px] my-[20px]">
                                            {t('Student_Admin_Edit_Token_Title')}
                                        </h3>
                                        <Table
                                            className="mt-2"
                                            columns={[
                                                {
                                                    key: 'tokenType',
                                                    dataIndex: 'tokenType',
                                                    title: t('Student_Admin_Edit_Token_Table_Type'),
                                                    render: (ele) => t(ele?.name)
                                                },
                                                {
                                                    key: 'credit',
                                                    dataIndex: 'credit',
                                                    title: t(
                                                        'Student_Admin_Edit_Token_Table_Amount'
                                                    )
                                                }
                                            ]}
                                            dataSource={tokens}
                                            pagination={false}
                                        />
                                        <h3 className="card-title pl-[20px] my-[20px]">
                                            {t('Student_Admin_Edit_TokenPurchased_Title')}
                                        </h3>
                                        <Table
                                            className="mt-2"
                                            columns={[
                                                {
                                                    key: 'name',
                                                    dataIndex: 'name',
                                                    title: t(
                                                        'Student_Admin_Edit_TokenPurchased_Table_Type'
                                                    )
                                                },
                                                {
                                                    key: 'totalAmount',
                                                    dataIndex: 'totalAmount',
                                                    title: t(
                                                        'Student_Admin_Edit_TokenPurchased_Table_Amount'
                                                    )
                                                }
                                            ]}
                                            dataSource={tokensUsed}
                                            pagination={false}
                                        />
                                        <div className="card-body">
                                            <div className="row clearfix">
                                                <div className="col-md-6 col-sm-12">
                                                    <div className="form-group">
                                                        <label>
                                                            {t(
                                                                'Student_Admin_Edit_TokenTopUp_Type'
                                                            )}
                                                        </label>
                                                        <select
                                                            className="form-control input-height"
                                                            name="tokenTypeId"
                                                            value={tokenTopupData.tokenTypeId}
                                                            onChange={(e) =>
                                                                setTokenTopupData({
                                                                    ...tokenTopupData,
                                                                    tokenTypeId: e.target.value
                                                                })
                                                            }>
                                                            <option value="">
                                                                {t(
                                                                    'Student_Admin_Edit_TokenTopUp_TypePlaceholder'
                                                                )}
                                                            </option>
                                                            {tokenTypeData?.map((ele) => (
                                                                <option
                                                                    value={ele?.id}
                                                                    key={ele?.id}>
                                                                    {t(ele?.name)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-md-6 col-sm-12">
                                                    <div className="form-group">
                                                        <label>
                                                            {t(
                                                                'Student_Admin_Edit_TokenTopUp_Quantity'
                                                            )}
                                                        </label>
                                                        <input
                                                            onChange={(e) =>
                                                                setTokenTopupData({
                                                                    ...tokenTopupData,
                                                                    amount: Number(e.target.value)
                                                                })
                                                            }
                                                            value={tokenTopupData?.amount}
                                                            type="number"
                                                            min="1"
                                                            className="form-control"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-sm-12">
                                                    <button
                                                        type="button"
                                                        className="mr-1 btn btn-primary"
                                                        onClick={() => {
                                                            if (!tokenTopupData.tokenTypeId) {
                                                                notification.warning({
                                                                    message: t(
                                                                        'Student_Admin_Edit_TokenTopUp_Validation_Type'
                                                                    )
                                                                });
                                                                return;
                                                            }
                                                            if (
                                                                !tokenTopupData.amount ||
                                                                tokenTopupData.amount <= 0
                                                            ) {
                                                                notification.warning({
                                                                    message: t(
                                                                        'Student_Admin_Edit_TokenTopUp_Validation_AmountTitle'
                                                                    ),
                                                                    description: t(
                                                                        'Student_Admin_Edit_TokenTopUp_Validation_AmountDescription'
                                                                    )
                                                                });
                                                                return;
                                                            }

                                                            tokensApi
                                                                .topup(tokenTopupData)
                                                                .then((res) => {
                                                                    notification.success({
                                                                        message: t(
                                                                            'Student_Admin_Edit_TokenTopUp_Success'
                                                                        )
                                                                    });
                                                                    setTokenTopupData({
                                                                        ...tokenTopupData,
                                                                        amount: 0,
                                                                        tokenTypeId:
                                                                            tokenTypeData.length > 0
                                                                                ? tokenTypeData[0]
                                                                                      .id
                                                                                : ''
                                                                    });
                                                                })
                                                                .then(() => {
                                                                    fetchStudentDetail();
                                                                })
                                                                .catch((err) => {
                                                                    notification.info({
                                                                        message: t(
                                                                            'Student_Admin_Edit_TokenTopUp_Failed'
                                                                        )
                                                                    });
                                                                    console.error(err);
                                                                });
                                                        }}>
                                                        {t('Student_Admin_Edit_TokenTopUp_Button')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="col-lg-4 col-md-12 col-sm-12">
                                <div className="col-sm-12">
                                    <button
                                        type="button"
                                        className="mr-1 btn btn-primary"
                                        onClick={handleSubmit(submitForm, onFormError)}>
                                        {t('Student_Admin_Edit_Button_Submit')}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => window.history.back()}>
                                        {t('Student_Admin_Edit_Button_Cancel')}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

EditComponent.displayName = 'Edit';

const Edit = (props) => {
    return (
        <ErrorWrapper>
            <EditComponent
                data-id="Edit-cpn"
                {...props}
            />
        </ErrorWrapper>
    );
};

export default Edit;
